import PlayerCardCondensed, {
  CondensedCardPlayer,
} from "@/design-system/PlayerCard/PlayerCardCondensed";
import { Popover } from "@/design-system/Popover/Popover";
import { useTranslation } from "@repo/shared/i18n";
import { StreamlineSource } from "@repo/shared/types";
import { ReactNode } from "react";

import { SourceChip } from "./SourceChip";

/** Every card in the panel, the current one included, at one size. */
export const SWAP_CARD_SCALE = 0.52;
/* Wide enough for the facts column beside a full row of four cards with
   their chips; a slot offering fewer gives up one column per missing card. */
const PANEL_WIDTH = 680;
const CARDS_PER_ROW = 4;
/* A card at SWAP_CARD_SCALE, its px-1.5, and the row's gap-2.5. */
const CARD_COLUMN = Math.round(190 * SWAP_CARD_SCALE) + 12 + 10;
/* Facts alone, when the slot has nothing to swap for. */
const FACTS_WIDTH = 260;

const panelWidth = (count: number) =>
  count === 0
    ? FACTS_WIDTH
    : PANEL_WIDTH -
      (CARDS_PER_ROW - Math.min(count, CARDS_PER_ROW)) * CARD_COLUMN;

/** One card a slot could hold instead of the one it shows. */
export type SwapOption = {
  player: CondensedCardPlayer;
  source: StreamlineSource;
  untradeable?: boolean;
};

/**
 * Who a slot holds and what else could fill it. Shared by the Streamline
 * solution and the solver's pitch: each hands over its own facts column and
 * options, and gets back the index of the one picked. The panel closes on the
 * swap it just made.
 *
 * On touch devices the design system presents this as a BottomSheet, so the
 * two columns stack there and the web mobile layout gets the same flow.
 */
export const PlayerSwapPopover = ({
  facts,
  options,
  onSelect,
  children,
}: {
  /** The current player's card, chip and crests, resolved by the caller. */
  facts: ReactNode;
  options: SwapOption[];
  onSelect: (optionIndex: number) => void;
  /** The card that opens the panel. */
  children: ReactNode;
}) => {
  const { t } = useTranslation("sbcs");
  return (
    <Popover
      placement="right"
      align="start"
      width={panelWidth(options.length)}
      negLabel=""
      posLabel=""
      newDesignSystem
      trigger={
        <button
          type="button"
          className="block rounded-md transition-[filter] duration-200 hover:brightness-110 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-neutral-500"
        >
          {children}
        </button>
      }
    >
      {(close) => (
        <div className="flex flex-col gap-4 touch-device:flex-row touch-device:gap-5">
          {/* Sized by its content: the card, the crest buttons and the chip. */}
          <div className="flex flex-col items-start gap-2 touch-device:shrink-0">
            <span className="text-xs font-bold uppercase tracking-wide text-neutral-500">
              {t("currentPlayer")}
            </span>
            {facts}
          </div>

          {options.length > 0 && (
            <div className="flex min-w-0 flex-1 flex-col gap-2 border-t border-neutral-800 pt-3 touch-device:border-l touch-device:border-t-0 touch-device:pl-5 touch-device:pt-0">
              <span className="text-xs font-bold uppercase tracking-wide text-neutral-500">
                {t("swapFor")}
              </span>
              {/* One row that scrolls sideways in the BottomSheet, bleeding
              to the sheet's edges; four per row beside the facts column on
              desktop, capped at two rows so a slot with more options scrolls
              rather than growing the panel taller than it is wide. */}
              <div className="-mx-5 flex gap-2.5 overflow-x-auto px-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden touch-device:mx-0 touch-device:grid touch-device:max-h-[380px] touch-device:grid-cols-[repeat(auto-fill,minmax(96px,1fr))] touch-device:overflow-x-hidden touch-device:overflow-y-auto touch-device:px-0">
                {options.map((option, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => {
                      onSelect(index);
                      close();
                    }}
                    /* Same column as the facts' card (no top padding, gap-2
                       before the chip), so the cards and chips line up. */
                    className="flex shrink-0 flex-col items-center gap-2 rounded-xl px-1.5 transition-[filter] duration-200 hover:brightness-110"
                  >
                    <PlayerCardCondensed
                      player={option.player}
                      scale={SWAP_CARD_SCALE}
                      priceSize="small"
                      untradeable={option.untradeable}
                    />
                    <SourceChip source={option.source} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Popover>
  );
};
